import Cookies from 'js-cookie';
import { trace, type Span, SpanStatusCode } from '@opentelemetry/api';

interface SpanContext {
  span: Span;
}

/**
 * Start a UI span that stays open until explicitly ended.
 */
function startUiSpan(
  type: 'readiness' | 'action',
  name: string,
  attributes: Record<string, unknown> = {}
): SpanContext | null {
  try {
    const tracer = trace.getTracer('qontojs-metrics');

    const span = tracer.startSpan(`ui.${type}.${name}`);
    span.setAttribute('spanmetrics', 'enabled');

    Object.entries(attributes).forEach(([key, value]) => {
      span.setAttribute(key, String(value));
    });

    return { span };
  } catch {
    return null;
  }
}

/**
 * End a span that was started with startUiSpan.
 */
function endSpan(spanContext: SpanContext | null): void {
  if (!spanContext?.span) {
    return;
  }

  try {
    spanContext.span.end();
  } catch {
    // Ignore errors
  }
}

/**
 * Record a global UI error event.
 */
function recordUiError(error: unknown, attributes: Record<string, unknown> = {}): void {
  try {
    const tracer = trace.getTracer('qontojs-metrics');

    const span = tracer.startSpan('ui.error.global');
    span.setAttribute('spanmetrics', 'enabled');

    // Add error details
    if (error instanceof Error) {
      span.setAttribute('error.type', error.name);
      span.setAttribute('error.message', error.message);
      if (error.stack) {
        span.setAttribute('error.stack', error.stack);
      }
    } else {
      span.setAttribute('error.type', 'UnknownError');
      span.setAttribute('error.message', String(error));
    }

    // Add custom attributes
    Object.entries(attributes).forEach(([key, value]) => {
      span.setAttribute(key, String(value));
    });

    span.setStatus({ code: SpanStatusCode.ERROR });
    span.end();
  } catch {
    // Ignore errors in telemetry recording
  }
}

class TelemetryManager {
  private activeSpans = new Map<string, SpanContext>();
  private userId: string | null = null;

  setUserContext(userId: string): void {
    this.userId = userId;
  }

  getUserId(): string | null {
    return this.userId;
  }

  /**
   * Start a UI readiness span and store it for later completion.
   *
   * @param name - The name of the page / component
   * @param attributes - Additional attributes to attach to the span
   * @returns The span context, or null if failed to create
   */
  startUiReadiness(name: string, attributes: Record<string, unknown> = {}): SpanContext | null {
    const spanContext = startUiSpan('readiness', name, attributes);
    if (spanContext) {
      this.activeSpans.set(`readiness.${name}`, spanContext);
    }
    return spanContext;
  }

  /**
   * End a UI readiness span that was previously started.
   */
  endUiReadiness(name: string): void {
    const spanContext = this.activeSpans.get(`readiness.${name}`);
    if (spanContext) {
      endSpan(spanContext);
      this.activeSpans.delete(`readiness.${name}`);
    }
  }

  /**
   * Start a UI action span and store it for later completion.
   *
   * @param name - The name of the action (e.g., 'login', 'submit-payment')
   * @param attributes - Additional attributes to attach to the span
   * @returns The span context, or null if failed to create
   */
  startUiAction(name: string, attributes: Record<string, unknown> = {}): SpanContext | null {
    const spanContext = startUiSpan('action', name, attributes);
    if (spanContext) {
      this.activeSpans.set(`action.${name}`, spanContext);
    }
    return spanContext;
  }

  /**
   * End a UI action span that was previously started.
   */
  endUiAction(name: string): void {
    const spanContext = this.activeSpans.get(`action.${name}`);
    if (spanContext) {
      endSpan(spanContext);
      this.activeSpans.delete(`action.${name}`);
    }
  }

  /**
   * Cleanup any active span and all child spans.
   * This is useful for route deactivation to ensure spans are properly ended.
   * For example, cleanup('transactions') will clean up 'transactions',
   * 'transactions.page-to-network', 'transactions.backend-wait', etc.
   */
  cleanup(name: string): void {
    // Clean up the parent span
    this.endUiReadiness(name);

    // Clean up all child spans that start with the parent name
    const prefix = `readiness.${name}.`;
    const childSpans = Array.from(this.activeSpans.keys()).filter(key => key.startsWith(prefix));

    childSpans.forEach(childSpanName => {
      // Remove the 'readiness.' prefix before calling endUiReadiness
      const spanName = childSpanName.replace(/^readiness\./, '');
      this.endUiReadiness(spanName);
    });
  }

  /**
   * Check if a span is currently active.
   *
   * @param name - The name of the span
   * @param type - The type of span ('readiness' or 'action')
   */
  hasActiveSpan(name: string, type: 'readiness' | 'action'): boolean {
    return this.activeSpans.has(`${type}.${name}`);
  }

  /**
   * Record a global UI error event with telemetry.
   *
   * @param error - The error object or unknown value
   * @param attributes - Additional attributes to attach to the error span
   */
  recordError(error: unknown, attributes: Record<string, unknown> = {}): void {
    const enrichedAttributes = { ...attributes };

    if (this.userId) {
      enrichedAttributes.user_id = this.userId;
    }

    const deviceId = Cookies.get('deviceId');
    if (deviceId) {
      enrichedAttributes.device_id = deviceId;
    }

    recordUiError(error, enrichedAttributes);
  }
}

// Export a singleton instance
export const telemetryManager = new TelemetryManager();
